<template>
	<!-- terra-draw holds only the shape being traced. Its model is a constant
	     empty list: a transient draw reports a ring and keeps nothing, so the
	     committed ring is drawn on this widget's own source below. Routing it
	     through terra-draw's store would make every commit a clear-and-re-add
	     of geometry the operator is not editing, and would fight the caller's
	     own redraw of the ring it just handed over. -->
	<MglTerradrawSource
		ref="drawer"
		:source-id="`${sourceId}-in-progress`"
		:show-control="false"
		:modes="terradrawModes"
		:active-mode="mode"
		:model-value="NO_FEATURES"
		@create="onCreate"
	/>
	<mgl-geo-json-source :source-id="sourceId" :data="committedFeatures">
		<mgl-fill-layer :layer-id="`${sourceId}-fill`" :paint="fillPaint as any" />
		<mgl-line-layer :layer-id="`${sourceId}-line`" :paint="linePaint as any" />
	</mgl-geo-json-source>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
	MglFillLayer,
	MglGeoJsonSource,
	MglLineLayer,
	useMap,
} from "vue-maplibre-gl";

import MglTerradrawSource from "@/components/MglTerradrawSource.vue";

import { outerRing, ringToPolygon } from "./geometry";
import type { AreaCommit, AreaMode } from "./types";

/**
 * The one way an operator traces a shape on the map.
 *
 * It is transient: a closed shape is reported as a ring and then dropped, so the
 * widget owns no geometry of its own and a caller redraws what it has committed
 * by passing it back. terra-draw (through `MglTerradrawSource`) is the only
 * engine, which is why this is a thin wrapper rather than a second draw
 * implementation: rectangle, polygon, circle and freehand are terra-draw's
 * modes, and the arm/disarm map behaviour (dragPan, boxZoom, double-click zoom,
 * the crosshair) is terra-draw's own.
 *
 * `append`/`subtract` are read at the gesture's start, from a capture-phase
 * `mousedown` on the canvas: an operator who presses Shift halfway through a drag
 * meant the same gesture, and an operator who releases it before the drag ends
 * meant the same one too.
 */

const props = defineProps<{
	/** The overlay source this draw owns; unique per map. */
	sourceId: string;
	/** The modes the surface offers; the first is the default. */
	modes: AreaMode[];
	/** The armed mode; `null` disarms and gives the map back. */
	mode: AreaMode | null;
	/** Shapes to redraw under the new one. */
	committed: GeoJSON.Polygon[];
	/**
	 * Circle mode's typed radius.
	 *
	 * terra-draw's circle is sized by its drag, so the drawn disc is what gets
	 * committed and there is no click-to-commit-with-a-typed-radius path: a
	 * degenerate drag emits nothing, and nothing is the honest answer (a
	 * zero-radius circle is a point, not an area). The prop is accepted so a
	 * caller whose form still owns a typed radius can pass it through unchanged;
	 * a surface that needs the typed value to win builds its ring with
	 * `circleRing` instead.
	 */
	radiusMeters?: number;
}>();

const emit = defineEmits<{
	commit: [commit: AreaCommit];
	cancel: [];
}>();

const drawer = ref<InstanceType<typeof MglTerradrawSource> | null>(null);

/** `render` registers the drawing layers and is terra-draw's idle mode, so it
 * always leads the list. */
const terradrawModes = computed(() => ["render", ...props.modes]);

/** The empty list terra-draw is given once, as one object: a fresh `[]` on every
 * render would read as a model change and make the control clear its store. */
const NO_FEATURES: GeoJSON.Feature[] = [];

const committedFeatures = computed<GeoJSON.FeatureCollection>(() => ({
	type: "FeatureCollection",
	features: props.committed.map((polygon) => ({
		type: "Feature",
		properties: {},
		geometry: polygon,
	})),
}));

/* The committed ring is drawn in the same weight terra-draw draws the one being
   traced, so handing a ring over does not look like the shape changing kind. */
const fillPaint = { "fill-color": "#3b82f6", "fill-opacity": 0.25 };
const linePaint = { "line-color": "#1d4ed8", "line-width": 2 };

/** Modifiers as the keyboard currently holds them... */
const held = { shiftKey: false, altKey: false };
/** ...and as the gesture in progress started with, which is what a commit
 * reports. */
const gesture = { shiftKey: false, altKey: false };

const mapInstance = useMap();
let canvas: HTMLCanvasElement | null = null;

function trackHeld(event: KeyboardEvent): void {
	held.shiftKey = event.shiftKey;
	held.altKey = event.altKey;
}

function onGestureStart(event: MouseEvent): void {
	held.shiftKey = event.shiftKey;
	held.altKey = event.altKey;
	gesture.shiftKey = held.shiftKey;
	gesture.altKey = held.altKey;
}

function onKeyDown(event: KeyboardEvent): void {
	const tag = (event.target as HTMLElement | null)?.tagName;
	if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
	trackHeld(event);
	if (event.key !== "Escape" || props.mode === null) return;
	// Arm/disarm is the caller's (`mode`), so disarming here means telling it,
	// not deciding for it; dropping terra-draw to its idle mode first stops the
	// half-drawn shape from taking the next click.
	drawer.value?.switchMode("render");
	emit("cancel");
}

function onCreate(feature: GeoJSON.Feature): void {
	const geometry = feature.geometry;
	const drawn =
		geometry?.type === "Polygon"
			? outerRing(geometry)
			: geometry?.type === "MultiPolygon" && geometry.coordinates[0]?.[0]
				? geometry.coordinates[0][0].map(([lng, lat]): [number, number] => [
						lng,
						lat,
					])
				: null;
	// A MultiPolygon's other polygons are ignored: a transient area is one ring,
	// and a caller that wants more draws more.
	const polygon = drawn ? ringToPolygon(drawn) : null;
	if (!polygon) return;
	const mode = props.mode ?? props.modes[0];
	if (!mode) return;
	const instance = drawer.value?.getTerraDrawInstance() ?? null;
	emit("commit", {
		ring: outerRing(polygon),
		mode,
		modifiers: { append: gesture.shiftKey, subtract: gesture.altKey },
	});
	// The shape is transient: the caller now owns the ring and draws it back
	// through `committed`. terra-draw's copy is dropped so a second draw does not
	// stack on top of the first. The clear waits a microtask because terra-draw
	// is still dispatching the finish event it just emitted from, and because the
	// caller has already swapped the committed ring in by now — clearing the
	// in-progress store cannot touch it.
	if (instance) {
		queueMicrotask(() => {
			try {
				instance.clear();
			} catch {
				// The widget unmounted between the commit and this microtask, so
				// there is no store left to clean up.
			}
		});
	}
}

onMounted(() => {
	window.addEventListener("keydown", onKeyDown);
	window.addEventListener("keyup", trackHeld);
});

watch(
	() => mapInstance.map,
	(instance) => {
		if (canvas) canvas.removeEventListener("mousedown", onGestureStart, true);
		canvas = instance ? instance.getCanvas() : null;
		if (canvas) canvas.addEventListener("mousedown", onGestureStart, true);
	},
	{ immediate: true },
);

onBeforeUnmount(() => {
	window.removeEventListener("keydown", onKeyDown);
	window.removeEventListener("keyup", trackHeld);
	if (canvas) canvas.removeEventListener("mousedown", onGestureStart, true);
});
</script>
